import { useEffect, useState } from "preact/hooks";
import {
  type ConversationDetail,
  getSharedConversation,
} from "../api/conversations";
import { Disclaimer } from "../components/Disclaimer";
import { FabText } from "../components/FabText";
import { Markdown } from "../components/Markdown";
import { Wordmark } from "../components/Wordmark";

type State =
  | { status: "loading" }
  | { status: "ready"; data: ConversationDetail }
  | { status: "error"; message: string };

/** Read-only view of a shared ruling (`/s/:id`), reachable without signing in. */
export function Shared({ id }: { id: string }) {
  const [state, setState] = useState<State>({ status: "loading" });

  useEffect(() => {
    let cancelled = false;
    setState({ status: "loading" });
    getSharedConversation(id)
      .then((data) => {
        if (!cancelled) setState({ status: "ready", data });
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setState({
            status: "error",
            message: error instanceof Error
              ? error.message
              : "Ruling not found.",
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  return (
    <div class="view view--chat">
      <header class="chat__header">
        <div class="chat__header-inner content">
          <Wordmark href="/ask" />
          <a class="btn btn--icon btn--ghost" href="/">
            Ask your own
          </a>
        </div>
      </header>

      <div class="chat__thread">
        <div class="chat__turns content">
          {state.status === "loading" && (
            <p class="ask__lede">Loading ruling…</p>
          )}

          {state.status === "error" && (
            <div class="notice" role="alert">
              <span>{state.message}</span>
            </div>
          )}

          {state.status === "ready" &&
            state.data.messages.map((message) => (
              <div
                class={`turn turn--${
                  message.role === "user" ? "user" : "judge"
                }`}
                key={message.id}
              >
                <span class="turn__role">
                  {message.role === "user" ? "You" : "Judge"}
                </span>
                <div class="turn__body">
                  {message.role === "user"
                    ? <FabText text={message.content} />
                    : (
                      <Markdown
                        text={message.content}
                        sources={message.meta?.sources}
                        cards={message.meta?.cards}
                      />
                    )}
                </div>
              </div>
            ))}
        </div>
        <Disclaimer />
      </div>
    </div>
  );
}
